import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaRegCalendarCheck } from '@react-icons/all-files/fa/FaRegCalendarCheck';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import ProfaseModal from '../../commons/ProfaseModal';
import Confirm, { useConfirm } from '../../commons/Confirm';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import { profaseDataGridSx } from '../../commons/profaseDataGridTheme';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  archivarPeriodo,
  getEntities,
  revertirArchivadoPeriodo,
} from '../../manager/profase-periodo.reducer';

const ITEMS_PER_PAGE = 15;

const TIPO_LABEL: Record<string, string> = {
  mes: 'Mes',
  anio: 'Año',
  semestre: 'Semestre',
};

const ESTADO_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  NUEVO: { label: 'Nuevo', bg: '#fef9c3', color: '#854d0e' },
  GENERADO: { label: 'Generado', bg: '#eff6ff', color: '#1d4ed8' },
  ARCHIVADO: { label: 'Archivado', bg: '#f1f5f9', color: '#475569' },
};

export interface ProfasePeriodoRow {
  id: number;
  tipo?: string;
  fecha?: string;
  desde?: string;
  hasta?: string;
  numero?: number;
  anio?: number;
  estado?: string;
  descripcion?: string;
}

function axiosErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; message?: string } | undefined;
    return data?.detail ?? data?.message ?? fallback;
  }
  return fallback;
}

function EstadoBadge({ estado }: { estado?: string }) {
  if (!estado) return <span style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>—</span>;
  const cfg = ESTADO_CONFIG[estado.toUpperCase()] ?? { label: estado, bg: '#f1f5f9', color: '#475569' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 6px',
        height: 18,
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1,
        background: cfg.bg,
        color: cfg.color,
        letterSpacing: '0.03em',
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </span>
  );
}

type PeriodoVista = 'GENERADO' | 'ARCHIVADO';

export type { PeriodoVista };

export function parsePeriodoVista(estado: string | null): PeriodoVista {
  return estado?.toUpperCase() === 'ARCHIVADO' ? 'ARCHIVADO' : 'GENERADO';
}

function PeriodoVistaToggle({
  value,
  onChange,
}: {
  value: PeriodoVista;
  onChange: (vista: PeriodoVista) => void;
}) {
  return (
    <div className="actividades-vista-toggle" role="group" aria-label="Vista de periodos">
      <div className="actividades-vista-toggle__options">
        <button
          type="button"
          className={`actividades-vista-toggle__btn${value === 'GENERADO' ? ' is-active' : ''}`}
          aria-pressed={value === 'GENERADO'}
          onClick={() => onChange('GENERADO')}
        >
          Generado
        </button>
        <button
          type="button"
          className={`actividades-vista-toggle__btn${value === 'ARCHIVADO' ? ' is-active' : ''}`}
          aria-pressed={value === 'ARCHIVADO'}
          onClick={() => onChange('ARCHIVADO')}
        >
          Archivado
        </button>
      </div>
    </div>
  );
}

export interface ProcesosPeriodosViewProps {
  active?: boolean;
  estadoVista: PeriodoVista;
  onEstadoVistaChange: (vista: PeriodoVista) => void;
  onSelectPeriodo: (periodo: ProfasePeriodoRow) => void;
  onArchivadoChange?: () => void;
  onBack?: () => void;
  backLabel?: string;
}

export function ProcesosPeriodosView({
  active = true,
  estadoVista,
  onEstadoVistaChange,
  onSelectPeriodo,
  onArchivadoChange,
  onBack,
  backLabel = 'Cerrar',
}: ProcesosPeriodosViewProps) {
  const dispatch = useAppDispatch();
  const entities = useAppSelector(s => s.profasePeriodo.entities) as ProfasePeriodoRow[];
  const totalItems = useAppSelector(s => s.profasePeriodo.totalItems);
  const loading = useAppSelector(s => s.profasePeriodo.loading);
  const updating = useAppSelector(s => s.profasePeriodo.updating);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: ITEMS_PER_PAGE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'anio', sort: 'desc' },
  ]);
  const [actionPeriodoId, setActionPeriodoId] = useState<number | null>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  const fetchPeriodos = useCallback(() => {
    const sortItem = sortModel[0] ?? { field: 'anio', sort: 'desc' as const };
    const filters: { filterName: string; filterValue: string }[] = [
      { filterName: 'estado', filterValue: estadoVista },
    ];

    dispatch(
      getEntities({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        sort: `${sortItem.field},${sortItem.sort}`,
        filters,
      }),
    );
  }, [dispatch, paginationModel, sortModel, estadoVista]);

  useEffect(() => {
    if (!active) return;
    fetchPeriodos();
  }, [active, fetchPeriodos]);

  useEffect(() => {
    if (!active) return;
    setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [active, estadoVista]);

  useEffect(() => {
    if (active) return;
    setPaginationModel({ page: 0, pageSize: ITEMS_PER_PAGE });
    setSortModel([{ field: 'anio', sort: 'desc' }]);
    setActionPeriodoId(null);
  }, [active]);

  useEffect(() => {
    setConfirmLoading(updating);
  }, [updating, setConfirmLoading]);

  const runArchivar = async (periodo: ProfasePeriodoRow) => {
    setActionPeriodoId(periodo.id);
    try {
      await dispatch(archivarPeriodo(periodo.id)).unwrap();
      showToast('success', `Periodo "${periodo.descripcion ?? periodo.id}" archivado.`);
      fetchPeriodos();
      onArchivadoChange?.();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudo archivar el periodo.'));
    } finally {
      setActionPeriodoId(null);
      dismiss();
    }
  };

  const handleArchivarClick = (periodo: ProfasePeriodoRow) => {
    ask(
      <>
        ¿Archivar el periodo <strong>{periodo.descripcion ?? periodo.id}</strong>?
        {' '}Las instancias en <strong>En Proceso</strong> o <strong>Finalizado</strong> pasarán a estado archivado.
      </>,
      () => { void runArchivar(periodo); },
      { title: 'Archivar periodo', confirmLabel: 'Archivar', variant: 'warning' },
    );
  };

  const handleRevertirClick = async (periodo: ProfasePeriodoRow) => {
    setActionPeriodoId(periodo.id);
    try {
      await dispatch(revertirArchivadoPeriodo(periodo.id)).unwrap();
      showToast('success', `Archivado revertido para "${periodo.descripcion ?? periodo.id}".`);
      fetchPeriodos();
      onArchivadoChange?.();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudo revertir el archivado.'));
    } finally {
      setActionPeriodoId(null);
    }
  };

  const cols: GridColDef[] = useMemo(
    () => [
      {
        field: 'descripcion',
        headerName: 'Descripción',
        flex: 1,
        minWidth: 200,
        renderCell: params => {
          const label = params.row.descripcion?.trim();
          if (!label) return <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
          return (
            <button
              type="button"
              className="btn btn-link p-0 text-start"
              style={{ fontSize: 13, textDecoration: 'none' }}
              onClick={() => onSelectPeriodo(params.row as ProfasePeriodoRow)}
            >
              {label}
            </button>
          );
        },
      },
      {
        field: 'tipo',
        headerName: 'Tipo',
        flex: 0.35,
        minWidth: 90,
        renderCell: params => {
          const tipo = params.row.tipo as string | undefined;
          if (!tipo) return <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
          return TIPO_LABEL[tipo.toLowerCase()] ?? tipo;
        },
      },
      {
        field: 'estado',
        headerName: 'Estado',
        flex: 0.45,
        minWidth: 100,
        sortable: false,
        renderCell: params => (
          <div className="procesos-periodos-estado-cell">
            <EstadoBadge estado={params.row.estado} />
          </div>
        ),
      },
      {
        field: '__actions',
        headerName: '',
        flex: 0.55,
        minWidth: 130,
        sortable: false,
        renderCell: params => {
          const row = params.row as ProfasePeriodoRow;
          const estado = String(row.estado ?? '').toUpperCase();
          const busy = updating && actionPeriodoId === row.id;

          return (
            <div className="procesos-periodos-actions-cell">
              {estado === 'GENERADO' && (
                <button
                  type="button"
                  className="btn btn-outline-warning btn-sm"
                  disabled={busy}
                  onClick={() => handleArchivarClick(row)}
                >
                  Archivar
                </button>
              )}
              {estado === 'ARCHIVADO' && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={busy}
                  onClick={() => { void handleRevertirClick(row); }}
                >
                  Revertir
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [onSelectPeriodo, updating, actionPeriodoId],
  );

  return (
    <>
      {onBack ? (
        <div style={s.header}>
          <h2 className="profase-modal-title m-0">
            <FaRegCalendarCheck style={{ marginRight: 8, verticalAlign: '-2px' }} />
            Periodos
          </h2>
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onBack}>
            {backLabel}
          </button>
        </div>
      ) : null}

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <p style={s.hint}>
        Selecciona un periodo para filtrar la grilla de procesos. Archivar solo aplica a periodos generados.
      </p>

      <div style={s.filters}>
        <PeriodoVistaToggle value={estadoVista} onChange={onEstadoVistaChange} />
      </div>

      <DataGrid
        rows={entities ?? []}
        columns={cols}
        getRowId={row => row.id}
        autoHeight
        pagination
        paginationMode="server"
        sortingMode="server"
        disableRowSelectionOnClick
        disableColumnMenu
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={model => {
          setSortModel(model.length > 0 ? model : [{ field: 'anio', sort: 'desc' }]);
        }}
        rowCount={totalItems}
        loading={loading}
        pageSizeOptions={[ITEMS_PER_PAGE]}
        localeText={{ noRowsLabel: 'Sin periodos' }}
        sx={{
          ...profaseDataGridSx,
          '& .procesos-periodos-estado-cell, & .procesos-periodos-actions-cell': {
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            lineHeight: 1,
          },
          '& .procesos-periodos-actions-cell': {
            gap: 6,
            flexWrap: 'wrap',
          },
        }}
      />
    </>
  );
}

interface ProcesosPeriodosModalProps {
  open: boolean;
  estadoVista: PeriodoVista;
  onEstadoVistaChange: (vista: PeriodoVista) => void;
  onClose: () => void;
  onSelectPeriodo: (periodo: ProfasePeriodoRow) => void;
  onArchivadoChange?: () => void;
}

export default function ProcesosPeriodosModal({
  open,
  estadoVista,
  onEstadoVistaChange,
  onClose,
  onSelectPeriodo,
  onArchivadoChange,
}: ProcesosPeriodosModalProps) {
  return (
    <ProfaseModal
      open={open}
      titleId="procesos-periodos-modal-title"
      size="wide"
      scroll
    >
      <div style={s.header}>
        <h2 id="procesos-periodos-modal-title" className="profase-modal-title m-0">
          <FaRegCalendarCheck style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Periodos
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>

      <ProcesosPeriodosView
        active={open}
        estadoVista={estadoVista}
        onEstadoVistaChange={onEstadoVistaChange}
        onSelectPeriodo={onSelectPeriodo}
        onArchivadoChange={onArchivadoChange}
      />
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  hint: {
    margin: '0 0 12px',
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
};
